import { USERS, PROPERTIES } from '../../../firebase';
import { mortgageFormSectionCalculator } from '../../utility/mortgageCalculator';
import { getNumberFromString } from '../../utility/formatNumbers';
import getSum from '../../utility/getSum';
import convertUnitMoney from '../../utility/convertUnitMoney';
import { getTableSum } from '../../utility/getTableSum';
import { isValue } from '../../utility/isValue';
import FormUIOptions from '../../components/formBuilder/formElements/formUIOptions';
import CMHCInsurancePremiums from '../../constants/cmhcPremiums';
import criteriaDefaults from '../../constants/investmentCriteria';
import getNumberWithCommas from '../../../../functions/createReport/getNumberWithCommas';
import calculations from './calculations';
import longTermDefaults from '../../constants/longTermDefaults';

const purchaseDefaults = {
  downPayment: {
    derivedFrom: 'purchasePrice',
    unit: '%',
    value: 20,
  },
  interestRate: 6,
  amortization: 30,
  buildingInspection: 600,
  appraisal: 750,
  legalFees: 2500,
};

const enterTotalDefault = false;

export default {
  messageOnFinish: 'Proceed to the next step to explore BRRRR Scenario',
  pathToSource: [
    {
      type: 'collection',
      value: USERS,
    },
    {
      type: 'user',
      value: 'uid',
    },
    {
      type: 'collection',
      value: PROPERTIES,
    },
    {
      type: 'param',
      value: 'propertyId',
    },
  ],
  inputs: {
    title: 'Flip Worksheet',
    description:
      'Add financial details about your property, mortgage and rehab',
    submitButtons: 'flipWorksheet',
    submitButtonLabel: 'Submit & Analyze',
    submitButtonDescription:
      "Submit your information and we'll analyze the details.",
    schema: [
      {
        sectionValue: 'purchaseDetails',
        sectionLabel: 'Purchase Details',
        sectionDescription: 'Add basic purchase details.',
        schema: [
          {
            type: FormUIOptions.Money,
            label: 'Asking Price',
            layout: 'default',
            name: 'askingPrice',
            unit: '$',
          },
          {
            type: FormUIOptions.Money,
            label: 'Purchase Price',
            layout: 'default',
            name: 'purchasePrice',
            unit: '$',
            required: true,
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Cash Purchase?',
            layout: 'default',
            name: 'cashPurchase',
            defaultValue: false,
          },
        ],
      },
      {
        sectionLabel: 'Financing Details',
        sectionDescription:
          'Add information about your mortgage(s). You can modify the default values below.',
        sectionValue: 'financingDetails',
        sectionCondition: {
          field: 'cashPurchase',
          isLocal: true,
          value: false,
        },
        // sectionDescription: 'Information about your mortgage(s)',
        schema: [
          {
            type: FormUIOptions.UnitMoney,
            label: 'Down Payment',
            layout: 'default',
            name: 'downPayment',
            derivedFrom: 'purchasePrice',
            selectOptions: FormUIOptions.selectOptions.percentage,
            defaultValue: purchaseDefaults.downPayment,
            required: true,
            condition: {
              field: 'cashPurchase',
              isLocal: true,
              value: false,
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Interest Rate',
            layout: 'default',
            name: 'interestRate',
            defaultValue: purchaseDefaults.interestRate,
            groupStart: true,
            unit: '%',
            required: true,
            condition: {
              field: 'cashPurchase',
              isLocal: true,
              value: false,
            },
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest-only?',
            layout: 'default',
            name: 'mortgageInterestOnly',
            defaultValue: false,
            required: true,
            condition: {
              field: 'cashPurchase',
              isLocal: true,
              value: false,
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Loan Period',
            layout: 'default',
            name: 'interestOnlyLoanPeriod',
            unit: 'years',
            condition: [
              {
                field: 'mortgageInterestOnly',
                isLocal: true,
                value: true,
              },
              {
                field: 'cashPurchase',
                isLocal: true,
                value: false,
              },
            ],
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest paid monthly?',
            layout: 'default',
            name: 'interestOnlyMonthly',
            required: true,
            description:
              'Is the interest on your loan paid monthly or at the end of the loan period?',
            defaultValue: false,
            condition: [
              {
                field: 'mortgageInterestOnly',
                isLocal: true,
                value: true,
              },
              {
                field: 'cashPurchase',
                isLocal: true,
                value: false,
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Amortization',
            layout: 'default',
            name: 'amortization',
            defaultValue: purchaseDefaults.amortization,
            description: 'Mortgage period',
            unit: 'years',
            required: true,
            condition: [
              {
                field: 'mortgageInterestOnly',
                isLocal: true,
                value: !true,
              },
              {
                field: 'cashPurchase',
                isLocal: true,
                value: false,
              },
            ],
          },

          {
            type: FormUIOptions.YesNo,
            label: 'Include CMHC Premium?',
            description:
              'Canada Mortgage and Housing Corporation insurance, for properties in Canada. If you are using the CMHC MLI select plan, you will need to enter the insurance premium amount manually.',
            name: 'shouldCalculateCMHC',
            layout: 'default',
            defaultValue: false,
            condition: [
              {
                field: 'cashPurchase',
                isLocal: true,
                value: false,
              },

              {
                field: 'mortgageInterestOnly',
                isLocal: true,
                value: false,
              },
              {
                field: 'propertyInCanada',
                isLocal: true,
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Calculate for me?',
            description:
              'If you select no, you will need to enter the insurance premium amount manually',
            name: 'shouldCalculateCMHCForMe',
            layout: 'default',
            defaultValue: true,
            condition: [
              {
                field: 'shouldCalculateCMHC',
                isLocal: true,
                value: true,
              },
              {
                field: 'cashPurchase',
                isLocal: true,
                value: false,
              },
              {
                field: 'mortgageInterestOnly',
                isLocal: true,
                value: false,
              },
              {
                field: 'propertyInCanada',
                isLocal: true,
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.Money,
            label: 'CMHC/Insurance Premium',
            layout: 'default',
            name: 'cmhcPremiumManual',
            unit: '$',
            condition: [
              {
                field: 'shouldCalculateCMHC',
                isLocal: true,
                value: true,
              },
              {
                field: 'shouldCalculateCMHCForMe',
                isLocal: true,
                value: false,
              },
              {
                field: 'cashPurchase',
                isLocal: true,
                value: false,
              },
              {
                field: 'mortgageInterestOnly',
                isLocal: true,
                value: false,
              },
              {
                field: 'propertyInCanada',
                isLocal: true,
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.Calculated,
            label: 'CMHC Premium',
            layout: 'default',
            name: 'cmhcPremium',
            unit: '$',
            derivedFrom: [
              'purchasePrice',
              'downPaymentAmount',
              'shouldCalculateCMHC',
              'shouldCalculateCMHCForMe',
              'cmhcPremiumManual',
              'mortgageInterestOnly',
              'cashPurchase',
              'propertyInCanada',
            ],
            calcFunction: (
              purchasePrice,
              downPaymentAmount,
              shouldCalculateCMHC,
              shouldCalculateCMHCForMe,
              cmhcPremiumManual,
              mortgageInterestOnly,
              cashPurchase,
              propertyInCanada
            ) => {
              if (!shouldCalculateCMHCForMe) {
                return cmhcPremiumManual;
              }
              if (
                !shouldCalculateCMHC ||
                mortgageInterestOnly ||
                cashPurchase ||
                propertyInCanada === 0
              ) {
                return null;
              }
              if (!isValue(purchasePrice, downPaymentAmount)) {
                return null;
              }

              const mortgageAmount = purchasePrice - downPaymentAmount;
              const ltv = mortgageAmount / purchasePrice;
              const ltvRounded = Math.round(ltv * 100) / 100;

              const premiums = CMHCInsurancePremiums.flip;

              switch (true) {
                case ltvRounded <= 0.75:
                  return mortgageAmount * premiums[0.75];
                case ltvRounded <= 0.8:
                  return mortgageAmount * premiums[0.8];
                case ltvRounded <= 0.85:
                  return mortgageAmount * premiums[0.85];
                case ltvRounded <= 0.9:
                  return mortgageAmount * premiums[0.9];
                default:
                  return mortgageAmount * premiums[0.95];
              }
            },
            condition: [
              {
                field: 'cashPurchase',
                isLocal: true,
                value: false,
              },
              {
                field: 'shouldCalculateCMHC',
                isLocal: true,
                value: true,
              },
              {
                field: 'mortgageInterestOnly',
                isLocal: true,
                value: false,
              },
              {
                field: 'propertyInCanada',
                isLocal: true,
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.TableModal,
            layout: 'default',
            itemName: 'Additional Loan',
            groupStart: true,
            name: 'purchaseAdditionalFinancing',
            modalDescription: 'Add information about your loan',
            condition: {
              field: 'cashPurchase',
              isLocal: true,
              value: [false, undefined, ''],
            },
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Loan Name',
                layout: 'default',
                name: 'loanNameTable',
                before: true,
              },
              {
                type: FormUIOptions.Money,
                label: 'Financing Amount',
                layout: 'default',
                name: 'mortgageAmountTable',
                required: true,
                unit: '$',
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Interest Rate',
                layout: 'default',
                name: 'interestRateTable',
                unit: '%',
                required: true,
                before: true,
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest-only?',
                layout: 'default',
                name: 'mortgageInterestOnlyTable',
                defaultValue: false,
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Loan Period',
                layout: 'default',
                name: 'interestOnlyLoanPeriodTable',
                unit: 'years',
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: true,
                  },
                ],
                before: true,
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest paid monthly?',
                layout: 'default',
                name: 'interestOnlyMonthlyTable',
                description:
                  'Is the interest on your loan paid monthly or at the end of the loan period?',
                defaultValue: false,
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: true,
                  },
                ],
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Amortization',
                layout: 'default',
                name: 'amortizationTable',
                description: 'Mortgage period',
                unit: 'years',
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: false,
                  },
                ],
                before: true,
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Monthly Payment',
                layout: 'default',
                name: 'monthlyPaymentTable',
                unit: '$',
                derivedFrom: [
                  'mortgageAmountTable',
                  'interestRateTable',
                  'mortgageInterestOnlyTable',
                  'interestOnlyMonthlyTable',
                  'amortizationTable',
                ],
                calcFunction: (
                  mortgageAmountTable,
                  interestRateTable,
                  mortgageInterestOnlyTable,
                  interestOnlyMonthlyTable,
                  amortizationTable
                ) => {
                  return mortgageFormSectionCalculator(
                    mortgageAmountTable,
                    interestRateTable,
                    mortgageInterestOnlyTable,
                    interestOnlyMonthlyTable,
                    amortizationTable
                  );
                },
                before: true,
              },
            ],
          },
          {
            type: FormUIOptions.CalculatedSection,
            name: 'mortgageCalculations',
            groupStart: true,
            schema: [
              {
                type: FormUIOptions.Calculated,
                label: 'Down Payment',
                groupStart: true,
                layout: 'default',
                name: 'downPaymentAmount',
                unit: '$',
                //in this case 'downPayment' is the original value calculated based on the first mortgage and downpayment %, and downPaymentAmount is the value after considering the second mortgage.
                derivedFrom: [
                  'downPayment',
                  'purchaseAdditionalFinancing',
                  'cashPurchase',
                  'purchasePrice',
                ],
                calcFunction: (
                  downPayment,
                  purchaseAdditionalFinancing,
                  cashPurchase,
                  purchasePrice
                ) => {
                  if (cashPurchase) {
                    return purchasePrice;
                  }

                  const initDownPayment = convertUnitMoney(downPayment);

                  const otherFinancingAmount = Array.isArray(
                    purchaseAdditionalFinancing
                  )
                    ? purchaseAdditionalFinancing.reduce((acc, curr) => {
                        return curr.mortgageAmountTable
                          ? acc + convertUnitMoney(curr.mortgageAmountTable)
                          : acc;
                      }, 0)
                    : 0;

                  return initDownPayment - otherFinancingAmount;
                },
                condition: {
                  field: 'cashPurchase',
                  isLocal: true,
                  value: false,
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Total Mortgage Amount',
                layout: 'default',
                name: 'totalMortgageAmount',
                unit: '$',
                condition: {
                  field: 'cashPurchase',
                  isLocal: true,
                  value: false,
                },
                derivedFrom: [
                  'purchasePrice',
                  'downPayment',
                  'cashPurchase',
                  'shouldCalculateCMHC',
                  'cmhcPremium',
                  'purchaseAdditionalFinancing',
                  'propertyInCanada',
                ],
                calcFunction: (
                  purchasePrice,
                  downPayment,
                  cashPurchase,
                  shouldCalculateCMHC,
                  cmhcPremium,
                  purchaseAdditionalFinancing,
                  propertyInCanada
                ) => {
                  if (cashPurchase) {
                    return 0;
                  }

                  const firstMortgage = purchasePrice - downPayment;

                  const otherFinancingAmount = Array.isArray(
                    purchaseAdditionalFinancing
                  )
                    ? purchaseAdditionalFinancing.reduce((acc, curr) => {
                        return curr.mortgageAmountTable
                          ? acc + convertUnitMoney(curr.mortgageAmountTable)
                          : acc;
                      }, 0)
                    : 0;

                  let totalMortgage = firstMortgage + otherFinancingAmount;

                  if (
                    shouldCalculateCMHC &&
                    cmhcPremium &&
                    propertyInCanada !== 0
                  ) {
                    return totalMortgage + cmhcPremium;
                  }
                  return totalMortgage;
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Total Monthly Payment',
                layout: 'default',
                name: 'totalMonthlyMortgagePayment',
                unit: '$',
                condition: {
                  field: 'cashPurchase',
                  isLocal: true,
                  value: false,
                },
                derivedFrom: [
                  'interestRate',
                  'purchasePrice',
                  'downPayment',
                  'amortization',
                  'mortgageInterestOnly',
                  'interestOnlyMonthly',
                  'cashPurchase',
                  'shouldCalculateCMHC',
                  'cmhcPremium',
                  'purchaseAdditionalFinancing',
                  'propertyInCanada',
                ],
                calcFunction: (
                  interestRate,
                  purchasePrice,
                  downPayment,
                  amortization,
                  mortgageInterestOnly,
                  interestOnlyMonthly,
                  cashPurchase,
                  shouldCalculateCMHC,
                  cmhcPremium,
                  purchaseAdditionalFinancing,
                  propertyInCanada
                ) => {
                  if (cashPurchase) {
                    return 0;
                  }

                  let mortgageAmount = null;

                  if (isValue(purchasePrice, downPayment)) {
                    mortgageAmount = purchasePrice - downPayment;

                    if (
                      shouldCalculateCMHC &&
                      cmhcPremium &&
                      propertyInCanada !== 0
                    ) {
                      mortgageAmount += cmhcPremium;
                    }
                  }

                  const firstMortgageMonthlyPayment =
                    mortgageFormSectionCalculator(
                      mortgageAmount,
                      interestRate,
                      mortgageInterestOnly,
                      interestOnlyMonthly,
                      amortization
                    );

                  const otherFinancingMonthlyPayment = Array.isArray(
                    purchaseAdditionalFinancing
                  )
                    ? purchaseAdditionalFinancing.reduce((acc, curr) => {
                        return curr.monthlyPaymentTable
                          ? acc + convertUnitMoney(curr.monthlyPaymentTable)
                          : acc;
                      }, 0)
                    : 0;

                  return (
                    firstMortgageMonthlyPayment + otherFinancingMonthlyPayment
                  );
                },
              },
            ],
          },
        ],
      },
      {
        sectionLabel: 'Closing Costs',
        sectionDescription:
          'Add information about your closing costs. Add, remove, or modify items to customize your costs.',
        sectionValue: 'cashRequiredToClose',
        schema: [
          {
            type: FormUIOptions.YesNo,
            hideLabel: true,
            yesLabel: 'Total',
            noLabel: 'Itemize',
            toggleColour: 'green',
            name: 'enterTotalClosingCosts',
            description: 'Enter closing costs as a single amount or itemize',
            defaultValue: enterTotalDefault,
            layout: 'default',
          },
          {
            type: FormUIOptions.Money,
            name: 'closingCostNotItemized',
            label: 'Closing Costs',
            description: 'Enter the total estimated amount of closing costs.',
            condition: {
              field: 'enterTotalClosingCosts',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.TableInline,
            layout: 'default',
            itemName: 'Closing Cost',
            label: 'Closing Costs',
            name: 'closingCosts',
            defaultItems: [
              {
                closingCostName: 'Building Inspection',
                closingCostAmount: purchaseDefaults.buildingInspection,
              },
              {
                closingCostName: 'Appraisal',
                closingCostAmount: purchaseDefaults.appraisal,
              },
              {
                closingCostName: 'Legal Fees',
                closingCostAmount: purchaseDefaults.legalFees,
              },
              {
                closingCostName: 'Reserve Fund',
                closingCostAmount: '',
              },
              {
                closingCostName: 'Environment Assessment',
                closingCostAmount: '',
              },
              {
                closingCostName: 'Land Transfer Tax',
                closingCostAmount: '',
              },
            ],
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Name',
                layout: 'default',
                name: 'closingCostName',
              },
              {
                type: FormUIOptions.Money,
                label: 'Amount',
                layout: 'default',
                name: 'closingCostAmount',
              },
            ],
            condition: {
              field: 'enterTotalClosingCosts',
              isLocal: true,
              value: false,
            },
          },
          {
            type: FormUIOptions.CalculatedSection,
            name: 'closingCostCalculations',
            groupStart: true,
            schema: [
              {
                type: FormUIOptions.Calculated,
                label: 'TOTAL',
                layout: 'default',
                name: 'totalCashRequiredToClose',
                unit: '$',
                derivedFrom: [
                  'closingCosts',
                  'closingCostNotItemized',
                  'enterTotalClosingCosts',
                ],
                calcFunction: (
                  closingCosts,
                  closingCostNotItemized,
                  enterTotalClosingCosts
                ) => {
                  if (enterTotalClosingCosts) {
                    return getNumberFromString(closingCostNotItemized);
                  }
                  return getTableSum(closingCosts, 'closingCostAmount');
                },
              },
            ],
          },
        ],
      },
      {
        sectionLabel: 'Rehab Details',
        sectionDescription:
          'Add project timeline, costs, and financing details for the rehab.',
        sectionValue: 'rehabDetails',
        schema: [
          {
            type: FormUIOptions.Number,
            label: 'Estimated Project Length',
            maxValue: 999,
            description:
              'Enter the estimated overall length of the rehab project',
            groupEnd: true,
            layout: 'default',
            name: 'rehabLength',
            required: true,
            unit: 'months',
          },

          {
            type: FormUIOptions.TableInline,
            label: '',
            description:
              'Add any additional rehab costs not covered by the rental income section',
            layout: 'default',
            itemName: 'Rehab Cost',
            name: 'additionalInvestment',
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Name',
                layout: 'default',
                name: 'additionalInvestmentItem',
                before: true,
                after: true,
              },
              {
                type: FormUIOptions.Money,
                label: 'Cost',
                layout: 'default',
                name: 'additionalInvestmentValue',
                unit: '$',
                before: true,
                after: true,
              },
            ],
          },

          {
            type: FormUIOptions.YesNo,
            label: 'Finance Rehab Costs?',
            description: 'Will you be financing the rehab costs?',
            layout: 'default',
            groupStart: true,
            name: 'isRehabFinanced',
          },
          {
            type: FormUIOptions.Money,
            label: 'Financing Amount',
            layout: 'default',
            name: 'mortgageAmountRehab',
            required: true,
            unit: '$',
            derivedFrom: ['totalRehabCosts', 'mortgageAmountRehab'],
            calcFunction: (totalRehabCosts, mortgageAmountRehab) => {
              if (isValue(mortgageAmountRehab)) {
                return mortgageAmountRehab;
              }
              return totalRehabCosts;
            },
            condition: {
              field: 'isRehabFinanced',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Interest Rate',
            layout: 'default',
            name: 'interestRateRehab',
            unit: '%',
            required: true,
            condition: {
              field: 'isRehabFinanced',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest-only?',
            layout: 'default',
            name: 'mortgageInterestOnlyRehab',
            defaultValue: false,
            condition: {
              field: 'isRehabFinanced',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Loan Period',
            layout: 'default',
            name: 'interestOnlyLoanPeriodRehab',
            unit: 'years',
            condition: [
              {
                field: 'mortgageInterestOnlyRehab',
                isLocal: true,
                value: true,
              },
              {
                field: 'isRehabFinanced',
                isLocal: true,
                value: true,
              },
            ],
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest paid monthly?',
            layout: 'default',
            name: 'interestOnlyMonthlyRehab',
            description:
              'Is the interest on your loan paid monthly or at the end of the loan period?',
            defaultValue: false,
            condition: [
              {
                field: 'mortgageInterestOnlyRehab',
                isLocal: true,
                value: true,
              },
              {
                field: 'isRehabFinanced',
                isLocal: true,
                value: true,
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Amortization',
            layout: 'default',
            name: 'amortizationRehab',
            required: true,
            description: 'Financing period',
            unit: 'years',
            condition: [
              {
                field: 'mortgageInterestOnlyRehab',
                isLocal: true,
                value: false,
              },
              {
                field: 'isRehabFinanced',
                isLocal: true,
                value: true,
              },
            ],
          },

          {
            type: FormUIOptions.TableModal,
            layout: 'default',
            groupStart: true,
            groupEnd: true,
            itemName: 'Additional Financing',
            condition: {
              field: 'isRehabFinanced',
              isLocal: true,
              value: true,
            },
            label: '',
            name: 'rehabCostFinancing',
            modalDescription: 'Add information about your loan',
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Loan Name',
                layout: 'default',
                name: 'loanNameTable',
                before: true,
              },
              {
                type: FormUIOptions.Money,
                label: 'Financing Amount',
                layout: 'default',
                name: 'mortgageAmountTable',
                required: true,
                unit: '$',
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Interest Rate',
                layout: 'default',
                name: 'interestRateTable',
                unit: '%',
                required: true,
                before: true,
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest-only?',
                layout: 'default',
                name: 'mortgageInterestOnlyTable',
                defaultValue: false,
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Loan Period',
                layout: 'default',
                name: 'interestOnlyLoanPeriodTable',
                unit: 'years',
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: true,
                  },
                ],
                before: true,
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest paid monthly?',
                layout: 'default',
                name: 'interestOnlyMonthlyTable',
                description:
                  'Is the interest on your loan paid monthly or at the end of the loan period?',
                defaultValue: false,
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: true,
                  },
                ],
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Amortization',
                layout: 'default',
                name: 'amortizationTable',
                description: 'Mortgage period',
                unit: 'years',
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: false,
                  },
                ],
                before: true,
              },

              {
                type: FormUIOptions.Calculated,
                label: 'Monthly Payment',
                layout: 'default',
                name: 'monthlyPaymentTable',
                unit: '$',
                derivedFrom: [
                  'mortgageAmountTable',
                  'interestRateTable',
                  'mortgageInterestOnlyTable',
                  'interestOnlyMonthlyTable',
                  'amortizationTable',
                ],
                calcFunction: (
                  mortgageAmountTable,
                  interestRateTable,
                  mortgageInterestOnlyTable,
                  interestOnlyMonthlyTable,
                  amortizationTable
                ) => {
                  return mortgageFormSectionCalculator(
                    mortgageAmountTable,
                    interestRateTable,
                    mortgageInterestOnlyTable,
                    interestOnlyMonthlyTable,
                    amortizationTable
                  );
                },
              },
            ],
          },
          {
            type: FormUIOptions.CalculatedSection,
            name: 'rehabCostCalculations',
            schema: [
              {
                type: FormUIOptions.Calculated,
                label: 'Total Rehab Costs',
                total: true,
                layout: 'default',
                derivedFrom: ['additionalInvestment'],
                name: 'totalRehabCosts',
                unit: '$',
                calcFunction: (additionalInvestment) => {
                  return getTableSum(
                    additionalInvestment,
                    'additionalInvestmentValue'
                  );
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Total Financing Amount',
                layout: 'default',
                name: 'totalRehabFinancingAmount',
                unit: '$',
                derivedFrom: ['rehabCostFinancing', 'mortgageAmountRehab'],
                calcFunction: (rehabCostFinancing, mortgageAmountRehab) => {
                  const additionalFinancing = getTableSum(
                    rehabCostFinancing,
                    'mortgageAmountTable'
                  );
                  const rehab = mortgageAmountRehab || 0;
                  return getSum(additionalFinancing, rehab);
                },
                condition: {
                  field: 'isRehabFinanced',
                  isLocal: true,
                  value: true,
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Monthly Payment',
                layout: 'default',
                name: 'totalRehabFinancingMonthlyPayment',
                unit: '$',

                derivedFrom: [
                  'rehabCostFinancing',
                  'mortgageAmountRehab',
                  'interestRateRehab',
                  'mortgageInterestOnlyRehab',
                  'interestOnlyMonthlyRehab',
                  'amortizationRehab',
                ],
                calcFunction: (
                  rehabCostFinancing,
                  mortgageAmountRehab,
                  interestRateRehab,
                  mortgageInterestOnlyRehab,
                  interestOnlyMonthlyRehab,
                  amortizationRehab
                ) => {
                  const additionalPayments = getTableSum(
                    rehabCostFinancing,
                    'monthlyPaymentTable'
                  );
                  const payment = mortgageFormSectionCalculator(
                    mortgageAmountRehab,
                    interestRateRehab,
                    mortgageInterestOnlyRehab,
                    interestOnlyMonthlyRehab,
                    amortizationRehab
                  );
                  return getSum(additionalPayments, payment);
                },
                condition: {
                  field: 'isRehabFinanced',
                  isLocal: true,
                  value: true,
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Rehab Cost Cash',
                description: 'Total rehab costs not covered by financing',
                layout: 'default',
                name: 'rehabCostUnfinanced',
                unit: '$',
                derivedFrom: [
                  'totalRehabCosts',
                  'totalRehabFinancingAmount',
                  'isRehabFinanced',
                ],
                calcFunction: (
                  totalRehabCosts,
                  totalRehabFinancingAmount,
                  isRehabFinanced
                ) => {
                  if (!isRehabFinanced) {
                    return totalRehabCosts;
                  }
                  return totalRehabCosts - (totalRehabFinancingAmount || 0);
                },
              },
            ],
          },
        ],
      },

      {
        sectionLabel: 'Carrying Costs',
        sectionDescription:
          'Calculate the carrying costs for the property during the rehab period',
        sectionValue: 'rehabClosingCosts',
        schema: [
          {
            type: FormUIOptions.YesNo,
            label: 'Enter Total Expenses?',
            layout: 'default',
            name: 'enterTotalCarryingExpenses',
            hideLabel: true,
            yesLabel: 'Enter Total',
            noLabel: 'Itemize',
            toggleColour: 'green',
            defaultValue: false,
          },
          {
            type: FormUIOptions.UnitMoney,
            label: 'Total Carrying Expenses',
            description:
              'Enter the total monthly/yearly holding expenses for the property during the rehab period.',
            layout: 'default',
            name: 'carryingExpensesNotItemized',
            selectOptions: FormUIOptions.selectOptions.monthly,
            derivedFrom: 'rentalIncomePreVacancy',
            condition: [
              {
                field: 'enterTotalCarryingExpenses',
                isLocal: true,
                value: true,
              },
            ],
          },
          {
            type: FormUIOptions.TableInline,
            label: 'Carrying Expenses',
            layout: 'default',
            name: 'carryingExpenses',
            condition: [
              {
                field: 'enterTotalCarryingExpenses',
                isLocal: true,
                value: false,
              },
            ],
            defaultItems: [
              {
                expenseName: 'Property Tax',
                expenseAmount: '',
              },
              {
                expenseName: 'Insurance',
                expenseAmount: '',
              },
              {
                expenseName: 'Repairs & Maintenance',
                expenseAmount: '',
              },
              {
                expenseName: 'Property Management',
                expenseAmount: '',
              },
              {
                expenseName: 'Heat',
                expenseAmount: '',
              },
              {
                expenseName: 'Hydro',
                expenseAmount: '',
              },
              {
                expenseName: 'Water',
                expenseAmount: '',
              },
              {
                expenseName: 'Garbage',
                expenseAmount: '',
              },
            ],
            itemName: 'Expense',
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Name',
                layout: 'default',
                name: 'expenseName',
              },
              {
                type: FormUIOptions.UnitMoney,
                label: 'Value',
                layout: 'default',
                name: 'expenseAmount',
                selectOptions: FormUIOptions.selectOptions.monthly,
              },
            ],
          },
          {
            type: FormUIOptions.CalculatedSection,
            name: 'carryingCostCalculations',
            groupStart: true,
            schema: [
              {
                type: FormUIOptions.Calculated,
                label: 'Monthly Loan Payments',
                description: 'Sum of all monthly loan payments',
                layout: 'default',
                name: 'monthlyLoanPaymentsAll',
                unit: '$',
                derivedFrom: [
                  'totalMonthlyMortgagePayment',
                  'totalRehabFinancingMonthlyPayment',
                  'isRehabFinanced',
                  'cashPurchase',
                ],
                calcFunction: (
                  totalMonthlyMortgagePayment,
                  totalRehabFinancingMonthlyPayment,
                  isRehabFinanced,
                  cashPurchase
                ) => {
                  if (cashPurchase && !isRehabFinanced) {
                    return 0;
                  }

                  if (cashPurchase && isRehabFinanced) {
                    return totalRehabFinancingMonthlyPayment;
                  }

                  if (!cashPurchase && !isRehabFinanced) {
                    return totalMonthlyMortgagePayment;
                  }

                  if (!cashPurchase && isRehabFinanced) {
                    return getSum(
                      totalMonthlyMortgagePayment || 0,
                      totalRehabFinancingMonthlyPayment || 0
                    );
                  }
                },
              },
              {
                //should be the difference between the amount of rent you would have received and the amount of rent you actually received due to the rehab
                type: FormUIOptions.Calculated,
                label: 'Total Carrying Costs',
                tooltipHeading: 'Total Carrying Costs (Estimated)',
                description:
                  'Your total monthly carrying expenses and loan payments multiplied by the length of the rehab period.',
                total: true,
                layout: 'default',
                name: 'totalCarryingCosts',
                unit: '$',
                derivedFrom: [
                  'carryingExpenses',
                  'carryingExpensesNotItemized',
                  'enterTotalCarryingExpenses',
                  'rehabLength',
                  'monthlyLoanPaymentsAll',
                ],
                calcFunction: (
                  carryingExpenses,
                  carryingExpensesNotItemized,
                  enterTotalCarryingExpenses,
                  rehabLength,
                  monthlyLoanPaymentsAll
                ) => {
                  if (!isValue(rehabLength)) {
                    return 0;
                  }

                  const totalLoanPayments =
                    (monthlyLoanPaymentsAll || 0) * rehabLength;

                  if (enterTotalCarryingExpenses) {
                    return (
                      (carryingExpensesNotItemized || 0) * rehabLength +
                      totalLoanPayments
                    );
                  }
                  return (
                    getTableSum(carryingExpenses, 'expenseAmount') *
                      rehabLength +
                    totalLoanPayments
                  );
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Total Rehab & Carrying Costs',
                total: true,
                layout: 'default',
                name: 'totalRehabCarryingGreater12',
                unit: '$',
                derivedFrom: ['totalRehabCosts', 'totalCarryingCosts'],
                calcFunction: (totalRehabCosts, totalCarryingCosts) => {
                  const rehab = totalRehabCosts || 0;
                  const carrying = totalCarryingCosts || 0;
                  return rehab + carrying;
                },
              },
            ],
          },
        ],
      },
      {
        sectionLabel: 'New Property Value',
        sectionDescription:
          'Choose a method to calculate the new property value. You can modify the default selection below.',
        sectionValue: 'capRate',
        schema: [
          {
            type: FormUIOptions.Money,
            label: 'ARV (After-Repair Value)',
            required: true,
            description:
              'Enter the estimated after repair value of the property',
            layout: 'default',
            name: 'newMarketValue',
          },
        ],
      },
      {
        sectionLabel: 'Assumptions',
        sectionDescription:
          'Assumptions about change in income, expenses and property value over time. You can modify the default values.',
        sectionValue: 'appreciationDetails',
        sectionCollapsed: true,
        schema: [
          {
            type: FormUIOptions.Number,
            label: 'Passive Appreciation',
            layout: 'default',
            defaultValue: longTermDefaults.appreciation,
            description:
              'Estimated annual passive appreciation of the property, expressed as a percentage of the purchase price',
            name: 'marketAppreciationPercent',
            unit: '%',
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Sale Cost',
            defaultValue: longTermDefaults.saleCost,
            name: 'saleCostPercent',
            description:
              'Estimated cost of selling the property, expressed as a percentage of the sale price',
          },
        ],
      },
    ],
  },

  calculations: calculations,

  keyMetrics: [
    {
      sectionValue: 'keyMetrics1',
      layout: 'row',
      schema: [
        {
          type: FormUIOptions.Money,
          label: 'Cash Needed',
          layout: 'default',
          name: 'totalCashInvestedOnPurchase',
          unit: '$',
        },
        {
          type: FormUIOptions.Money,
          label: 'Total Profit',
          layout: 'default',
          name: 'totalProfitFlip',
          dynamic: true,
          unit: '$',
        },

        {
          type: FormUIOptions.Number,
          label: 'ROI',
          layout: 'default',
          name: 'flipROI',
          unit: '%',
        },
        {
          type: FormUIOptions.Number,
          label: 'Annualized ROI',
          layout: 'default',
          name: 'flipROIAnnualized',
          unit: '%',
        },
      ],
    },
  ],
};
